import React, { useState, useCallback } from 'react';
import PlatformBadge from '../PlatformBadge';
import TypeBadge from '../TypeBadge';
import CountdownTimer from '../CountdownTimer';
import { PLATFORMS } from '../../constants/platforms';
import { PlatformIconComponent } from '../icons/PlatformIcons';
import { affiliateUrl } from '../../utils/affiliateUrl';
import styles from './GameCard.module.css';

function isExpiringSoon(availableUntil) {
  if (!availableUntil) return false;
  const ms = new Date(availableUntil).getTime() - Date.now();
  return ms > 0 && ms < 86400000;
}

function isNewDeal(availableFrom) {
  if (!availableFrom) return false;
  const ms = Date.now() - new Date(availableFrom).getTime();
  return ms >= 0 && ms < 48 * 3600000;
}

function GameCard({ game, claimed = false, onToggleClaimed, wishlisted = false, onToggleWishlist }) {
  const [imgError, setImgError]     = useState(false);
  const [copied, setCopied]         = useState(false);
  const [shared, setShared]         = useState(false);
  const platform                    = PLATFORMS[game.platform];
  const platformColor               = platform?.color || '#3B82F6';
  const expiringSoon                = isExpiringSoon(game.availableUntil);
  const isPermanent                 = game.type === 'permanently-free';
  const isSteamPromo                = game.platform === 'steam' && game.type === 'free-to-keep' && !game.availableUntil;
  const isItchioLimited             = game.platform === 'itchio' && game.type === 'free-to-keep';
  const isNew                       = isNewDeal(game.availableFrom);
  const showExpiringBanner          = expiringSoon || isSteamPromo;

  const claimHref = affiliateUrl(game.claimUrl, game.platform);

  const handleCopy = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(game.claimUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  }, [game.claimUrl]);

  const handleShare = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${game.title} — Free on ${platform?.label || game.platform}`,
          text: `${game.title} is free right now! Grab it before it's gone.`,
          url: game.claimUrl,
        });
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      } catch { /* user cancelled share */ }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(game.claimUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* clipboard unavailable */ }
    }
  }, [game, platform]);

  return (
    <div
      className={`${styles.card} ${claimed ? styles.cardClaimed : ''}`}
      style={{ '--platform-color': platformColor }}
    >
      {showExpiringBanner && (
        <div className={styles.expiringBanner}>
          {expiringSoon ? 'Expiring Soon' : 'Expiring Soon · Check Steam for date'}
        </div>
      )}
      {isItchioLimited && !expiringSoon && (
        <div className={styles.itchioBanner}>Limited Availability</div>
      )}

      <div className={styles.thumbnailWrapper}>
        {isNew && !claimed && <span className={styles.newBadge}>NEW</span>}
        {claimed && <span className={styles.claimedBadge}>✓ Claimed</span>}
        {/* Wishlist bookmark button */}
        <button
          className={`${styles.wishlistBtn} ${wishlisted ? styles.wishlistBtnActive : ''}`}
          onClick={e => { e.preventDefault(); e.stopPropagation(); onToggleWishlist?.(); }}
          title={wishlisted ? 'Remove from saved' : 'Save for later'}
          aria-label={wishlisted ? 'Remove from saved' : 'Save for later'}
          aria-pressed={wishlisted}
        >
          <BookmarkIcon filled={wishlisted} />
        </button>
        {!imgError && game.thumbnail ? (
          <img
            src={game.thumbnail}
            alt={game.title}
            className={styles.thumbnail}
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            className={styles.thumbnailFallback}
            style={{ backgroundColor: platformColor + '18' }}
          >
            <PlatformIconComponent platform={game.platform} size={48} className={styles.fallbackIcon} />
          </div>
        )}
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{game.title}</h3>

        {game.originalPrice && (
          <p className={styles.originalPrice}>
            <s>{game.originalPrice}</s>
            <span className={styles.freeTag}> FREE</span>
          </p>
        )}

        <div className={styles.badges}>
          <PlatformBadge platform={game.platform} />
          {!isPermanent && <TypeBadge type={game.type} />}
        </div>

        {!isPermanent && (
          <div className={styles.timer}>
            <CountdownTimer
              availableUntil={game.availableUntil}
              platform={game.platform}
              type={game.type}
            />
          </div>
        )}

        <div className={styles.cardActions}>
          <a
            href={claimHref}
            target="_blank"
            rel="noopener noreferrer"
            className={`${styles.claimButton} ${claimed ? styles.claimButtonClaimed : ''}`}
            onClick={onToggleClaimed}
          >
            {claimed ? '✓ Claimed' : isPermanent ? 'Play Free' : 'Claim Now'}
          </a>
          <button
            className={`${styles.copyBtn} ${copied ? styles.copyBtnCopied : ''}`}
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy link'}
            aria-label="Copy deal link"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
          <button
            className={`${styles.shareBtn} ${shared ? styles.copyBtnCopied : ''}`}
            onClick={handleShare}
            title="Share deal"
            aria-label="Share this deal"
          >
            <ShareIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function BookmarkIcon({ filled }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
    </svg>
  );
}

export default GameCard;
